import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Download,
  ExternalLink,
  Copy,
  Edit2,
  Calendar,
  Link as LinkIcon,
} from 'lucide-react';
import { Button } from '../../../../components/Button';
import { Badge } from '../../../../components/Badge';
import { ExportDropdown } from './ExportDropdown';
import { QRCodeData, AnalyticsSummary } from '../types';
import { QRCodePreview, QRCodePreviewHandle } from '../../../../components/qr/QRCodePreview';
import { getQrTargetUrl } from '../../../../utils/qr';
import { copyToClipboard } from '../../../../utils/clipboard';
import { useImageDataUrl } from '../../../../utils/imageDataUrl';
import toast from 'react-hot-toast';

interface QRCodeHeaderProps {
  qrCode: QRCodeData;
  analytics: AnalyticsSummary | undefined;
  onEditClick: () => void;
}

export function QRCodeHeader({ qrCode, analytics, onEditClick }: QRCodeHeaderProps) {
  const navigate = useNavigate();
  const qrPreviewRef = useRef<QRCodePreviewHandle>(null);
  const targetUrl = getQrTargetUrl(qrCode);
  // Resolved as a data: URI (not the raw URL) for this thumbnail specifically because
  // QRCodePreview's live embed of the *same* URL (if this image is also the QR's logo)
  // needs a crossOrigin fetch -- a plain thumbnail <img> and a crossOrigin fetch racing
  // for the identical URL can trip a Chromium cache-write race. See utils/imageDataUrl.ts.
  const fallbackImageDataUrl = useImageDataUrl(qrCode.fallback_image_url);

  const handleDownloadQR = async () => {
    try {
      await qrPreviewRef.current?.download(`${qrCode.title}-qr`, 'png');
      toast.success('QR code downloaded!');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const handleCopyUrl = async () => {
    try {
      await copyToClipboard(targetUrl);
      toast.success('URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const handleOpenUrl = () => {
    window.open(targetUrl, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-8"
    >
      <button
        onClick={() => navigate('/dashboard/qr-codes')}
        className="flex items-center gap-2 text-[--clinqr-text-muted] hover:text-[--clinqr-primary] mb-4 transition-colors text-sm font-medium group"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to QR Codes
      </button>

      <div className="flex flex-col lg:flex-row lg:items-center gap-6 bg-white p-6 rounded-2xl border-2 border-gray-200 shadow-sm max-w-7xl mx-auto">
        {/* QR Code Preview and Quick Actions */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative group">
            <div className="w-32 h-32 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 overflow-hidden transition-all group-hover:border-blue-100 group-hover:shadow-md">
              <QRCodePreview
                ref={qrPreviewRef}
                value={targetUrl}
                backgroundColor={qrCode.background_color || '#FFFFFF'}
                foregroundColor={qrCode.foreground_color || '#000000'}
                logoUrl={qrCode.logo_image_id ? qrCode.logo_image_url || undefined : qrCode.custom_logo_url}
                dotsType={qrCode.qr_style?.dots_type}
                cornersSquareType={qrCode.qr_style?.corners_square_type}
                cornersDotType={qrCode.qr_style?.corners_dot_type}
                className="w-full h-full p-1.5"
              />
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleCopyUrl} className="h-8 px-3 hover:bg-gray-100 transition-colors">
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copy Link
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadQR} className="h-8 px-3 hover:brightness-90 transition-all">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Download
            </Button>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="hidden lg:block w-px h-24 bg-gray-100" />

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <h1 className="text-3xl font-bold text-gray-900 truncate">{qrCode.title}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              {qrCode.has_rules && (
                <Badge variant="rules">
                  {qrCode.rules_count} RULE{qrCode.rules_count !== 1 ? 'S' : ''}
                </Badge>
              )}
              {qrCode.password_protected && (
                <Badge variant="protected">PROTECTED</Badge>
              )}
            </div>
          </div>
          
          {qrCode.description && (
            <p className="text-gray-500 mb-4 text-sm leading-relaxed line-clamp-2 max-w-2xl">
              {qrCode.description}
            </p>
          )}

          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-8 h-8 rounded-lg bg-[--clinqr-primary]/5 flex items-center justify-center">
                <LinkIcon className="w-4 h-4 text-[--clinqr-primary]" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 leading-none mb-1">Default Destination</span>
                {qrCode.fallback_image_id && fallbackImageDataUrl ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={fallbackImageDataUrl}
                      alt="Destination"
                      className="w-6 h-6 object-contain rounded border border-gray-200 bg-white"
                    />
                    <span className="font-medium">Image</span>
                  </div>
                ) : (
                  <span className="font-medium truncate max-w-[240px]">{qrCode.fallback_url}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-8 h-8 rounded-lg bg-[--clinqr-primary]/5 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-[--clinqr-primary]" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 leading-none mb-1">Created</span>
                <span className="font-medium">{new Date(qrCode.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Actions */}
        <div className="flex flex-wrap items-center gap-2 lg:flex-col lg:items-stretch lg:justify-center border-t lg:border-t-0 lg:border-l border-gray-100 pt-4 lg:pt-0 lg:pl-6">
          <Button variant="secondary" size="sm" onClick={onEditClick} className="flex-1 lg:flex-none hover:bg-gray-100 transition-colors">
            <Edit2 className="w-4 h-4 mr-2" />
            Edit QR
          </Button>
          <Button variant="secondary" size="sm" onClick={handleOpenUrl} className="flex-1 lg:flex-none hover:bg-gray-100 transition-colors">
            <ExternalLink className="w-4 h-4 mr-2" />
            Visit Link
          </Button>
          <ExportDropdown
            qrCode={qrCode as any}
            analytics={analytics}
          />
        </div>
      </div>
    </motion.div>
  );
}
